/**
 * useAdminAuth Hook
 *
 * Status: [IMPLEMENTED]
 * Task: T2.8.3 - Implement auth check
 *
 * Custom hook for managing admin authentication state.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  isAuthenticated as checkAuth,
  login as apiLogin,
  logout as apiLogout,
  verifyToken,
} from '../services/adminApi';

/**
 * Hook for managing admin authentication
 * @param {Object} options
 * @param {boolean} options.requireAuth - If true, redirect to login if not authenticated
 * @returns {Object} Auth state and methods
 */
export const useAdminAuth = ({ requireAuth = false } = {}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(checkAuth());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Verify token on mount
  useEffect(() => {
    const verify = async () => {
      if (checkAuth()) {
        const valid = await verifyToken();
        setIsAuthenticated(valid);
        if (!valid && requireAuth) {
          navigate('/azzmin-login', {
            state: { from: location.pathname, message: 'Session expired. Please login again.' },
          });
        }
      } else {
        setIsAuthenticated(false);
        if (requireAuth) {
          navigate('/azzmin-login', {
            state: { from: location.pathname },
          });
        }
      }
      setIsLoading(false);
    };

    verify();
  }, [requireAuth, navigate, location.pathname]);

  /**
   * Login with password
   * @param {string} password
   * @returns {Promise<boolean>} Success status
   */
  const login = useCallback(async (password) => {
    setIsLoading(true);
    setError(null);

    const result = await apiLogin(password);

    if (result.success) {
      setIsAuthenticated(true);
      // Redirect to the page they tried to access, or admin home
      const from = location.state?.from || '/azzmin-access';
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }

    setIsLoading(false);
    return result.success;
  }, [navigate, location.state?.from]);

  /**
   * Logout and redirect to login
   */
  const logout = useCallback(() => {
    apiLogout();
    setIsAuthenticated(false);
    navigate('/azzmin-login');
  }, [navigate]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    clearError,
  };
};

export default useAdminAuth;
